import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, Download, Trash2, Eye, Camera, Send,
  ArrowLeft, FileText, Building2, Mail as MailIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  sent: 'bg-green-500/20 text-green-400',
  pending: 'bg-amber-500/20 text-amber-400',
  failed: 'bg-red-500/20 text-red-400',
};

const TYPE_LABELS = {
  project: 'Single Project',
  multi_project: 'Multi-Project',
  general: 'General Overhead',
};

export default function CapturesAdmin() {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [captures, setCaptures] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedCapture, setSelectedCapture] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Filters
  const [company, setCompany] = useState('all');
  const [captureType, setCaptureType] = useState('all');
  const [destType, setDestType] = useState('all');
  const [status, setStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Summary
  const [summary, setSummary] = useState({ total: 0, livecosts: 0, xero: 0, con: 0, dev: 0, failed: 0 });

  const loadCaptures = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (company !== 'all') params.set('company', company);
      if (captureType !== 'all') params.set('capture_type', captureType);
      if (destType !== 'all') params.set('destination_type', destType);
      if (status !== 'all') params.set('status', status);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      params.set('limit', '100');

      const res = await api.get(`/captures?${params.toString()}`);
      const items = res.data.captures || [];
      setCaptures(items);
      setTotal(res.data.total || 0);

      // Calculate summary from results
      setSummary({
        total: items.length,
        livecosts: items.filter(c => c.destination_type === 'livecosts').length,
        xero: items.filter(c => c.destination_type === 'xero').length,
        con: items.filter(c => c.company === 'CON').length,
        dev: items.filter(c => c.company === 'DEV').length,
        failed: items.filter(c => c.sent_status === 'failed').length,
      });
    } catch {
      toast.error('Failed to load captures');
    } finally {
      setLoading(false);
    }
  }, [api, company, captureType, destType, status, startDate, endDate]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/projects');
      return;
    }
    loadCaptures();
  }, [isAdmin, navigate, loadCaptures]);

  const handleDelete = async (captureId) => {
    if (!window.confirm('Delete this capture record?')) return;
    try {
      await api.delete(`/captures/${captureId}`);
      toast.success('Capture deleted');
      loadCaptures();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (company !== 'all') params.set('company', company);
      if (captureType !== 'all') params.set('capture_type', captureType);
      if (destType !== 'all') params.set('destination_type', destType);
      if (status !== 'all') params.set('status', status);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);

      const res = await api.get(`/captures/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `captures_export_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Export failed');
    }
  };

  const openDetail = (capture) => {
    setSelectedCapture(capture);
    setDetailOpen(true);
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    try { return format(new Date(isoStr), 'dd/MM/yyyy HH:mm'); } catch { return isoStr.slice(0, 16); }
  };

  return (
    <div className="min-h-screen bg-[#1E2530] p-4 md:p-6" data-testid="captures-admin">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Camera className="w-6 h-6 text-[#F5A623]" /> Captures
            </h1>
            <p className="text-sm text-gray-400">{total} capture records</p>
          </div>
        </div>
        <Button onClick={handleExport} variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total', value: summary.total, color: 'text-white' },
          { label: 'LiveCosts', value: summary.livecosts, color: 'text-blue-400' },
          { label: 'Xero', value: summary.xero, color: 'text-green-400' },
          { label: 'CON', value: summary.con, color: 'text-purple-400' },
          { label: 'Failed', value: summary.failed, color: 'text-red-400' },
        ].map(s => (
          <Card key={s.label} className="bg-[#2A3240] border-white/10">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger className="w-[130px] bg-[#2A3240] border-white/20 text-white text-sm">
            <SelectValue placeholder="Company" />
          </SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white focus:bg-[#F5A623]/20">All Companies</SelectItem>
            <SelectItem value="DEV" className="text-white focus:bg-[#F5A623]/20">DEV</SelectItem>
            <SelectItem value="CON" className="text-white focus:bg-[#F5A623]/20">CON</SelectItem>
          </SelectContent>
        </Select>
        <Select value={captureType} onValueChange={setCaptureType}>
          <SelectTrigger className="w-[140px] bg-[#2A3240] border-white/20 text-white text-sm">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white focus:bg-[#F5A623]/20">All Types</SelectItem>
            <SelectItem value="project" className="text-white focus:bg-[#F5A623]/20">Single Project</SelectItem>
            <SelectItem value="multi_project" className="text-white focus:bg-[#F5A623]/20">Multi-Project</SelectItem>
            <SelectItem value="general" className="text-white focus:bg-[#F5A623]/20">General</SelectItem>
          </SelectContent>
        </Select>
        <Select value={destType} onValueChange={setDestType}>
          <SelectTrigger className="w-[140px] bg-[#2A3240] border-white/20 text-white text-sm">
            <SelectValue placeholder="Destination" />
          </SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white focus:bg-[#F5A623]/20">All Destinations</SelectItem>
            <SelectItem value="livecosts" className="text-white focus:bg-[#F5A623]/20">LiveCosts</SelectItem>
            <SelectItem value="xero" className="text-white focus:bg-[#F5A623]/20">Xero</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[130px] bg-[#2A3240] border-white/20 text-white text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white focus:bg-[#F5A623]/20">All Status</SelectItem>
            <SelectItem value="sent" className="text-white focus:bg-[#F5A623]/20">Sent</SelectItem>
            <SelectItem value="pending" className="text-white focus:bg-[#F5A623]/20">Pending</SelectItem>
            <SelectItem value="failed" className="text-white focus:bg-[#F5A623]/20">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-[150px] bg-[#2A3240] border-white/20 text-white text-sm" placeholder="Start date" />
        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-[150px] bg-[#2A3240] border-white/20 text-white text-sm" placeholder="End date" />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>
      ) : captures.length === 0 ? (
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="py-12 text-center">
            <Camera className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No captures found</p>
            <p className="text-xs text-gray-500 mt-1">Captures will appear here once users start photographing receipts</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="captures-table">
            <thead>
              <tr className="border-b border-white/10 text-gray-400 text-xs uppercase">
                <th className="text-left py-3 px-3">Date</th>
                <th className="text-left py-3 px-3">User</th>
                <th className="text-left py-3 px-3">Type</th>
                <th className="text-left py-3 px-3">Company</th>
                <th className="text-left py-3 px-3">Project</th>
                <th className="text-left py-3 px-3">Destination</th>
                <th className="text-left py-3 px-3">Status</th>
                <th className="text-left py-3 px-3">Files</th>
                <th className="text-left py-3 px-3">Pages</th>
                <th className="text-right py-3 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {captures.map(c => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => openDetail(c)}>
                  <td className="py-3 px-3 text-gray-300">{formatDate(c.created_at)}</td>
                  <td className="py-3 px-3 text-white">{c.user_name}</td>
                  <td className="py-3 px-3 text-gray-300">{TYPE_LABELS[c.capture_type] || c.capture_type}</td>
                  <td className="py-3 px-3"><Badge className="bg-gray-500/20 text-gray-300">{c.company}</Badge></td>
                  <td className="py-3 px-3 text-gray-300">{c.linked_project_ref || '-'}</td>
                  <td className="py-3 px-3 text-gray-400 text-xs">{c.destination_email || '-'}</td>
                  <td className="py-3 px-3"><Badge className={STATUS_COLORS[c.sent_status] || ''}>{c.sent_status}</Badge></td>
                  <td className="py-3 px-3 text-gray-300">{c.file_count}</td>
                  <td className="py-3 px-3 text-gray-300">{c.output_pdf?.page_count || '-'}</td>
                  <td className="py-3 px-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(c)} className="text-gray-400 hover:text-white h-7 w-7 p-0"><Eye className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} className="text-red-400 hover:text-red-300 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#F5A623]" />
              Capture Detail
            </DialogTitle>
          </DialogHeader>
          {selectedCapture && (
            <div className="space-y-4">
              {selectedCapture.sent_status === 'sent' && (
                <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3" data-testid="admin-sent-banner">
                  <p className="text-sm text-green-400">Sent successfully to {selectedCapture.destination_type} on {formatDate(selectedCapture.sent_at)}</p>
                  {selectedCapture.destination_email && (
                    <p className="text-xs mt-1 text-green-300/70" data-testid="admin-sent-to-line">Sent to: {selectedCapture.destination_email}</p>
                  )}
                  {selectedCapture.bcc_email && (
                    <p className="text-xs text-green-300/70" data-testid="admin-bcc-line">BCC: {selectedCapture.bcc_email}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-400">User:</span> <span className="text-white ml-2">{selectedCapture.user_name}</span></div>
                <div><span className="text-gray-400">Date:</span> <span className="text-white ml-2">{formatDate(selectedCapture.created_at)}</span></div>
                <div><span className="text-gray-400">Type:</span> <span className="text-white ml-2">{TYPE_LABELS[selectedCapture.capture_type]}</span></div>
                <div><span className="text-gray-400">Company:</span> <span className="text-white ml-2">{selectedCapture.company === 'DEV' ? 'Lux Developments' : 'Lux Construction'}</span></div>
                {selectedCapture.linked_project_ref && (
                  <div className="col-span-2"><span className="text-gray-400">Project:</span> <span className="text-white ml-2">{selectedCapture.linked_project_ref} — {selectedCapture.linked_project_name}</span></div>
                )}
                <div><span className="text-gray-400">Destination:</span> <span className="text-white ml-2">{selectedCapture.destination_type}</span></div>
                <div><span className="text-gray-400">Status:</span> <Badge className={`ml-2 ${STATUS_COLORS[selectedCapture.sent_status]}`}>{selectedCapture.sent_status}</Badge></div>
                <div className="col-span-2"><span className="text-gray-400">Email:</span> <span className="text-white ml-2">{selectedCapture.destination_email || 'Not configured'}</span></div>
                {selectedCapture.cc_email && (
                  <div className="col-span-2"><span className="text-gray-400">CC:</span> <span className="text-white ml-2">{selectedCapture.cc_email}</span></div>
                )}
                {selectedCapture.sent_at && (
                  <div className="col-span-2"><span className="text-gray-400">Sent at:</span> <span className="text-white ml-2">{formatDate(selectedCapture.sent_at)}</span></div>
                )}
                {selectedCapture.failure_reason && (
                  <div className="col-span-2"><span className="text-gray-400">Error:</span> <span className="text-red-400 ml-2 text-xs">{selectedCapture.failure_reason}</span></div>
                )}
                <div className="col-span-2"><span className="text-gray-400">Notes:</span> <span className="text-white ml-2">{selectedCapture.notes || '(none)'}</span></div>
              </div>

              {/* Output PDF */}
              <div>
                <p className="text-gray-400 text-xs uppercase mb-2">Output PDF</p>
                {selectedCapture.output_pdf ? (
                  <div className="bg-[#1E2530] p-3 rounded space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-5 h-5 text-red-400" />
                      <span className="text-white font-medium">{selectedCapture.output_pdf.filename}</span>
                      <span className="text-gray-400 text-xs ml-auto">{selectedCapture.output_pdf.page_count} page(s) | {Math.round((selectedCapture.output_pdf.size_bytes || 0) / 1024)}KB</span>
                    </div>
                    {selectedCapture.output_pdf.onedrive_url && (
                      <a href={selectedCapture.output_pdf.onedrive_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#F5A623] text-sm hover:underline">
                        <Download className="w-3.5 h-3.5" /> Open in OneDrive
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No PDF generated</p>
                )}
              </div>

              {/* Input Files (for audit) */}
              {selectedCapture.input_files && selectedCapture.input_files.length > 0 && (
                <div>
                  <p className="text-gray-400 text-xs uppercase mb-2">Input Files ({selectedCapture.input_files.length})</p>
                  <div className="space-y-1">
                    {selectedCapture.input_files.map((f, i) => (
                      <div key={f.filename ? `${f.filename}-${i}` : i} className="flex items-center gap-2 text-sm bg-[#1E2530] p-2 rounded">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-white">{f.filename}</span>
                        <span className="text-gray-500 text-xs ml-auto">{f.mime_type} | {Math.round((f.size_bytes || 0) / 1024)}KB</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-white/10">
                <Button size="sm" variant="outline" onClick={() => { handleDelete(selectedCapture.id); setDetailOpen(false); }} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
